import JSZip from "jszip";
import { firstValueFrom } from "rxjs";
import { Inject, Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { SubjectLocation, SubjectModel } from "../../_objects/subject-model";
import { InspectionReport } from "../../_objects/inspection-report";
import { InspectionSchema } from "../../_offline/models/inspection.schema";
import { InspectionImageSchema } from "../../_offline/models/inspectionImage.schema";
import { SubjectSchema } from "../../_offline/models/subject.schema";
import { OfflineUtilsService } from "../../_offline/services/offline-utils.service";
import { CommonSnackBarService } from "../common-snack-bar.service";
import { FileSystemAccessService } from "../file-system-access.service";
import { InspectionDbService } from "../database/inspection-db.service";
import { InspectionImageDbService } from "../database/inspection-image-db.service";
import { SubjectDbService } from "../database/subject-db.service";
import { SpinnerService } from "../spinner.service";
import { IndividualReportDbService } from "../database/individual-report-db.service";
import { IndividualReportSchema } from "../../_offline/models/individual-report.schema";
import { INSPECTION_REPORT_STATUS_CODE } from "../../app.constants";
import { NetworkStatusService } from "../network-status.service";
import { IVisolFilePickerService, VISOL_FILE_PICKER_SERVICE } from "../../common-dialog/file-manager/visol-file-picker.service";
import { buildStructureFolders, INSPECTION_STRUCTURE_FORMAT, InspectionStructureFormat } from "../../_utils/inspection-structure.utils";

// Same suffixes the cloud export uses (SharedManagementService), so both zips share one naming convention
const RETOUCHED_FILE_SUFFIX = "_retouched";
const MEASURED_FILE_SUFFIX = "_measured";

@Injectable({
  providedIn: "root"
})
export class InspectionExportOfflineService {
  constructor(
    private readonly inspectionDbService: InspectionDbService,
    private readonly inspectionImageDbService: InspectionImageDbService,
    private readonly subjectDbService: SubjectDbService,
    private readonly fileSystemAccessService: FileSystemAccessService,
    private readonly offlineUtilsService: OfflineUtilsService,
    private readonly networkStatusService: NetworkStatusService,
    private readonly spinnerService: SpinnerService,
    private readonly snackBar: CommonSnackBarService,
    private readonly translate: TranslateService,
    private readonly individualReportDbService: IndividualReportDbService,
    @Inject(VISOL_FILE_PICKER_SERVICE) private readonly visolFilePicker: IVisolFilePickerService
  ) {}

  /**
   * Exports the inspection images and reports as a zip. The location folders follow the chosen structure format,
   * legacy flat folders or the nested display name tree; "Unallocated" and "report" stay at the first level either way.
   */
  public async exportInspection(inspectionId: string, format: InspectionStructureFormat = INSPECTION_STRUCTURE_FORMAT.LEGACY): Promise<void> {
    try {
      this.spinnerService.show();

      const { inspection, subject, images } = await this.loadInspectionData(inspectionId);
      const locations = this.resolveSubjectModelLocations(subject);
      const rootName = this.buildZipRootName(inspection);
      const { zip, leafFolderPaths } = this.initializeZipStructure(rootName, locations, format);

      await this.addImagesToZip(zip, rootName, images, leafFolderPaths);
      await this.addReportsToZip(zip, rootName, inspectionId);

      const saved = await this.saveZip(zip, rootName);
      if (saved) {
        this.snackBar.open(this.translate.instant("shareInspection.success"));
      }
    } catch (error) {
      console.error(error);
      this.snackBar.open(this.translate.instant("shareInspection.error"));
    } finally {
      this.spinnerService.hide();
    }
  }

  private async loadInspectionData(inspectionId: string): Promise<{
    inspection: InspectionSchema;
    subject: SubjectSchema;
    images: InspectionImageSchema[];
  }> {
    const inspection = await firstValueFrom(this.inspectionDbService.getInspection(inspectionId, true));
    const subject = await firstValueFrom(this.subjectDbService.getOne(inspection.subjectId));
    const images = await firstValueFrom(this.inspectionImageDbService.getAll(inspectionId));
    return { inspection, subject, images };
  }

  /** The whole location tree of the inspection's subject model, so the folders can be nested when the format asks for it. */
  private resolveSubjectModelLocations(subject: SubjectSchema): SubjectLocation[] {
    if (!subject?.subjectModel) {
      return [];
    }
    const subjectModel = new SubjectModel();
    subjectModel.setSubjectModelSchema(subject.subjectModel);
    return subjectModel.locations ?? [];
  }

  private buildZipRootName(inspection: InspectionSchema): string {
    return (inspection.title || "inspection").replace(/ /g, "").trim() || "inspection";
  }

  /** Creates the location folders and returns, per leaf location identifier, the folder its images go to. */
  private initializeZipStructure(
    rootName: string,
    locations: SubjectLocation[],
    format: InspectionStructureFormat
  ): { zip: JSZip; leafFolderPaths: Map<string, string> } {
    const zip = new JSZip();
    const leafFolderPaths = new Map<string, string>();

    for (const folder of buildStructureFolders(locations, format)) {
      const folderPath = `${rootName}/${folder.pathSegments.join("/")}`;
      zip.folder(folderPath);
      if (folder.isLeaf) {
        leafFolderPaths.set(folder.location.identifier, folderPath);
      }
    }

    zip.folder(`${rootName}/Unallocated`);
    zip.folder(`${rootName}/report`);
    return { zip, leafFolderPaths };
  }

  private async addImagesToZip(zip: JSZip, rootName: string, images: InspectionImageSchema[], leafFolderPaths: Map<string, string>): Promise<void> {
    const tasks = images.map(async (image) => {
      const targetFolder = zip.folder(leafFolderPaths.get(image.subjectLocationId) ?? `${rootName}/Unallocated`);

      if (!targetFolder || !image.localFilePath) {
        return;
      }

      await Promise.all([
        this.addImageEntry(targetFolder, image.fileName, this.fileSystemAccessService.getFile(image.localFilePath, image.fileName, image.mimeType)),
        this.addImageEntry(
          targetFolder,
          this.buildVariantEntryName(image.fileName, RETOUCHED_FILE_SUFFIX),
          this.offlineUtilsService.getModifiedImageObj(image)
        ),
        this.addImageEntry(
          targetFolder,
          this.buildVariantEntryName(image.fileName, MEASURED_FILE_SUFFIX),
          this.offlineUtilsService.getMeasurementImageObj(image)
        )
      ]);
    });

    await Promise.all(tasks);
  }

  private async addImageEntry(targetFolder: JSZip, entryName: string, filePromise: Promise<File | null>): Promise<void> {
    try {
      const file = await filePromise;
      if (!file) {
        return;
      }
      const buffer = await file.arrayBuffer();
      targetFolder.file(entryName, buffer);
    } catch (error) {
      console.error(error);
    }
  }

  private buildVariantEntryName(fileName: string, suffix: string): string {
    const dotIndex = fileName.lastIndexOf(".");
    if (dotIndex <= 0) {
      return `${fileName}${suffix}`;
    }
    return `${fileName.slice(0, dotIndex)}${suffix}${fileName.slice(dotIndex)}`;
  }

  private async addReportsToZip(zip: JSZip, rootName: string, inspectionId: string): Promise<void> {
    const reportsFolder = zip.folder(`${rootName}/report`);
    if (!reportsFolder) return;

    const allReports = await firstValueFrom(this.individualReportDbService.getByInspectionIds([inspectionId]));
    const completedReports = allReports.filter((r) => r.status === INSPECTION_REPORT_STATUS_CODE.COMPLETED);

    await Promise.all(completedReports.map((report) => this.addReportPdfToFolder(reportsFolder, report)));
  }

  private async addReportPdfToFolder(reportsFolder: JSZip, report: IndividualReportSchema): Promise<void> {
    if (!report.reportId || report.version == null) {
      return;
    }

    const file = await this.readReportPdfFile(report);
    if (!file) {
      console.error(`Report PDF not found for report ${report.reportId} (version ${report.version}); it is skipped in the exported archive.`);
      return;
    }

    const buffer = await file.arrayBuffer();
    const entryName = this.resolveUniqueReportEntryName(reportsFolder, report);
    reportsFolder.file(`${entryName}.pdf`, buffer);
  }

  private async readReportPdfFile(report: IndividualReportSchema): Promise<File | null> {
    for (const pdfName of this.buildReportPdfFileNameCandidates(report)) {
      const pdfPath = `inspections\\${report.inspectionId}\\reports\\${report.reportId}\\${pdfName}`;
      try {
        return await this.fileSystemAccessService.getFile(pdfPath, pdfName, "application/pdf");
      } catch {
        // Try the next known naming convention
      }
    }
    return null;
  }

  // The PDF on disk is named after finalReportTitle when the report row carries one,
  // after the title (with "-version" from version 2 on) when it was generated on this device,
  // or report-{version}.pdf as the legacy fallback.
  private buildReportPdfFileNameCandidates(report: IndividualReportSchema): string[] {
    const candidates: string[] = [];

    const addValidTitle = (title: string | null | undefined) => {
      const normalizedTitle = (title ?? "").trim();
      if (normalizedTitle && normalizedTitle.toLowerCase() !== "null" && normalizedTitle.toLowerCase() !== "undefined") {
        candidates.push(`${normalizedTitle}.pdf`);
      }
    };

    addValidTitle(report.finalReportTitle);
    addValidTitle(report.title ? InspectionReport.getFinalReportTitle(report.title, report.version) : null);
    candidates.push(`report-${report.version}.pdf`);

    return [...new Set(candidates)];
  }

  private resolveUniqueReportEntryName(reportsFolder: JSZip, report: IndividualReportSchema): string {
    const baseTitle = (report.title ?? "").trim();
    const isValidTitle = baseTitle && baseTitle.toLowerCase() !== "null" && baseTitle.toLowerCase() !== "undefined";
    const baseName = isValidTitle ? baseTitle : `report-${report.reportId}-${report.version}`;

    const sanitizedBaseName = baseName.replace(/[<>:"/\\|?*]/g, "_");
    let entryName = sanitizedBaseName;
    let cnt = 0;
    while (reportsFolder.file(`${entryName}.pdf`) !== null) {
      cnt++;
      entryName = `${sanitizedBaseName} (${cnt})`;
    }
    return entryName;
  }

  private async saveZip(zip: JSZip, rootName: string): Promise<boolean> {
    const blob = await zip.generateAsync({ type: "blob" });
    const zipFileName = `${rootName || "inspection"}.zip`;
    const zipFile = new File([blob], zipFileName, { type: "application/zip" });

    // The archive is built; hide the spinner so it does not overlay the destination dialog.
    this.spinnerService.hide();

    if (this.networkStatusService.useApplicationFileManager()) {
      // The zip goes to the Monitor root or a USB drive — inspection folders under Monitor are not valid destinations.
      return this.visolFilePicker.exportFileWithResult({ content: zipFile, filename: zipFileName, restrictSystemDriveToRoot: true });
    }

    const savePath = await this.fileSystemAccessService.browseSavePath(zipFileName);
    if (!savePath) {
      return false;
    }

    await this.fileSystemAccessService.saveFileToFullPath(zipFile, savePath);
    return true;
  }
}
